import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { ref, query, orderByKey, limitToLast, get, onValue } from 'firebase/database';
import { db } from '../lib/firebase';
import { Word, CEFRLevel, SynonymActivity, FillBlankActivity, TrueFalseActivity, WordDay } from '../types';
import { mockSynonymActivities, mockFillBlankActivities, mockTrueFalseActivities } from '../data/mockData';
import { useAuth } from './AuthContext';
import { details } from 'framer-motion/client';
import { searchWord } from '../services/dictionary';
import SpeakActivity from '../components/activities/SpeakActivity';

interface WordContextType {
  currentWord: WordDay | null;
  synonymActivity: SynonymActivity | null;
  fillBlankActivity: FillBlankActivity | null;
  trueFalseActivity: TrueFalseActivity | null;
  loadWordOfTheDay: () => void;
  loadWordByDate: (date: string) => Promise<void>;
  loadRandomWord: () => void;
  activitiesCompleted: {
    synonym: boolean;
    fillBlank: boolean;
    trueFalse: boolean;
    speaky: boolean;
  };
  markActivityCompleted: (type: 'synonym' | 'fillBlank' | 'trueFalse' | 'speaky') => void;
  setActivitiesCompleted: (activities: {
    synonym: boolean;
    fillBlank: boolean;
    trueFalse: boolean;
    speaky: boolean;
  }) => void;
  resetActivities: () => void;
  wordLoading: boolean;
  wordHistory: Word[];
  getWordOfTheDay: string | null;
}

export const WordContext = createContext<WordContextType | undefined>(undefined);

export const WordProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [currentWord, setCurrentWord] = useState<WordDay | null>(null);
  const [wordHistory, setWordHistory] = useState<WordDay[]>([]);
  const [synonymActivity, setSynonymActivity] = useState<SynonymActivity | null>(null);
  const [fillBlankActivity, setFillBlankActivity] = useState<FillBlankActivity | null>(null);
  const [trueFalseActivity, setTrueFalseActivity] = useState<TrueFalseActivity | null>(null);
  const [speakyActvity, setSpeakyActivity] = useState<any>(null);
  const [getWordOfTheDay, setWordOfDay] = useState<string | null>(null);
  const [wordLoading, setWordLoading] = useState(false);
  const [activitiesCompleted, setActivitiesCompleted] = useState({
    synonym: false,
    fillBlank: false,
    trueFalse: false,
    speaky: false
  });
  const isInitialMount = useRef(true);
  const isLoadingRef = useRef(false);
  const isLoadedRef = useRef(false);

  useEffect(() => {
      // Prevent double execution on mount
      if (isInitialMount.current) {
        isInitialMount.current = false;
        // subscribeToWordHistory();
      }
  }, []);

  const subscribeToWordHistory = () => {
    const historyRef = ref(db, 'wordHistory');
    const historyQuery = query(historyRef, orderByKey());

    onValue(historyQuery, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const words: Word[] = [];
        Object.entries(data).forEach(([date, dateData]: [string, any]) => {
          console.log('Date:', date);
          console.log('Date data:', dateData);
          if (dateData.word) {
            words.push({
              ...dateData.word,
              date: new Date(dateData.timestamp)
            });
          }
        });
        setWordHistory(words.sort((a, b) => b.date.getTime() - a.date.getTime()));
      }
    });
  };

  const loadWordOfTheDay = useCallback(async () => {
    if (isLoadingRef.current || isLoadedRef.current) return;
    isLoadingRef.current = true;
    setWordLoading(true);
    try {
      const historyRef = ref(db, 'wordHistory');
      const lastWordQuery = query(historyRef, orderByKey(), limitToLast(1));
      const snapshot = await get(lastWordQuery);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const lastDate = Object.keys(data)[0];
        const wordData = data[lastDate];

        if (wordData && wordData.word) {
          // Split by '/' and take the first word (e.g., "ok/okay" -> "ok")
          const cleanWord = wordData.word.split('/')[0].trim();
          
          // Validate cleanWord is not empty
          if (!cleanWord) {
            console.warn('Word of the day is empty after cleaning');
            setCurrentWord(null);
            return;
          }
          
          try {
            const wordDetails = wordData.details || await searchWord(cleanWord.replace(/[^\w\s]/gi, ''));
            
            // Validate wordDetails has at least a word property
            if (wordDetails && wordDetails.word) {
              setCurrentWord({
                word: cleanWord,
                timestamp: wordData.timestamp,
                audioUrl: wordData.audioUrl,
                podcastText: wordData.podcastText,
                details: wordDetails,
                date: lastDate
              });
              setWordOfDay(cleanWord);
            } else {
              console.warn('Word details missing word property');
              setCurrentWord(null);
            }
          } catch (error) {
            console.error('Error fetching word details:', error);
            setCurrentWord(null);
          }
        } else {
          setCurrentWord(null);
          console.log('No word found');
        }
      } else {
        console.log('No word of the day found');
        setCurrentWord(null);
      }
    } catch (error) {
      console.error('Error loading word of the day:', error);
      setCurrentWord(null);
    } finally {
      isLoadingRef.current = false;
      isLoadedRef.current = true;
      setWordLoading(false);
    }
  }, []);

  const loadWordByDate = useCallback(async (date: string) => {
    setWordLoading(true);
    try {
      const historyRef = ref(db, `wordHistory/${date}`);
      const snapshot = await get(historyRef);
      
      if (snapshot.exists()) {
        const wordData = snapshot.val();

        if (wordData && wordData.word) {
          const cleanWord = wordData.word.split('/')[0].trim();
          
          if (!cleanWord) {
            console.warn('Word is empty after cleaning');
            setCurrentWord(null);
            return;
          }
          
          try {
            const wordDetails = await searchWord(cleanWord.replace(/[^\w\s]/gi, ''));
            
            if (wordDetails && wordDetails.word) {
              setCurrentWord({
                word: cleanWord,
                timestamp: wordData.timestamp,
                audioUrl: wordData.audioUrl,
                podcastText: wordData.podcastText,
                details: wordDetails,
                date: date
              });
              // We do not change getWordOfTheDay because that should remain today's word
            } else {
              console.warn('Word details missing word property');
              setCurrentWord(null);
            }
          } catch (error) {
            console.error('Error fetching word details:', error);
            setCurrentWord(null);
          }
        } else {
          setCurrentWord(null);
          console.log('No word found for this date');
        }
      } else {
        console.log('No word found for this date');
        setCurrentWord(null);
      }
    } catch (error) {
      console.error('Error loading word by date:', error);
      setCurrentWord(null);
    } finally {
      setWordLoading(false);
    }
  }, []);

  const loadRandomWord = useCallback(async () => {

    setWordLoading(true);
    try {
      const historyRef = ref(db, 'wordHistory');
      const snapshot = await get(historyRef);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        const dates = Object.keys(data).filter(date => {
          if (!currentWord?.date) return true;
          const wordDate = new Date(date);
          const currentDate = new Date(currentWord.timestamp);
          return wordDate.getTime() !== currentDate.getTime();
        });
        
        if (dates.length > 0) {
          const data = snapshot.val();
          console.log('Word of the day data:', data);
          const lastDate = Object.keys(data)[0];
          console.log('Last date:', lastDate);
          // Split by '/' and take the first word (e.g., "ok/okay" -> "ok")
          const cleanWord = data[lastDate].word.split('/')[0].trim();

          console.log('Word data:', cleanWord);
          
          if (cleanWord) {
            const wordDetails = await searchWord(cleanWord.replace(/[^\w\s]/gi, ''));
            setCurrentWord({
              word: cleanWord,
              timestamp: data[lastDate].timestamp,
              audioUrl: data[lastDate].audioUrl,
              podcastText: data[lastDate].podcastText,
              details: wordDetails,
              date: lastDate
            });
          } else {
            console.log('No word found');
            setCurrentWord(null);
          }
        }
      }
    } catch (error) {
      console.error('Error loading random word:', error);
    } finally {
      setWordLoading(false);
    }
  }, [currentWord]);

  const markActivityCompleted = (type: 'synonym' | 'fillBlank' | 'trueFalse' | 'speaky') => {
    setActivitiesCompleted(prev => ({
      ...prev,
      [type]: true
    }));
  };

  const resetActivities = () => {
    setActivitiesCompleted({
      synonym: false,
      fillBlank: false,
      trueFalse: false,
      speaky: false
    });
  };

  return (
    <WordContext.Provider value={{
      currentWord,
      synonymActivity,
      fillBlankActivity,
      trueFalseActivity,
      loadWordOfTheDay,
      loadWordByDate,
      loadRandomWord,
      activitiesCompleted,
      markActivityCompleted,
      resetActivities,
      wordLoading,
      wordHistory,
      setActivitiesCompleted,
      getWordOfTheDay,
    }}>
      {children}
    </WordContext.Provider>
  );
};

export const useWord = () => {
  const context = useContext(WordContext);
  if (context === undefined) {
    throw new Error('useWord must be used within a WordProvider');
  }
  return context;
};